import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Security } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import styled from '@emotion/styled'
import { configurationStoreSelector } from 'selectors/storeSelectors'

export interface AuthProviderProps {
  children?: React.ReactNode
}

const AuthProviderWrapper = styled(Security)`
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

export const AuthProvider = (props: AuthProviderProps) => {
  const configuration = useSelector(configurationStoreSelector)
  const navigate = useNavigate()

  if (!configuration.authentication || !configuration.authentication.enabled) {
    return <Fragment>{props.children}</Fragment>
  }

  const { authServer, applicationId, redirectUri } = configuration.authentication

  const customAuthHandler = () => {
    navigate('/login')
  }

  const restoreOriginalUri = async () => {
    const relativeUri = toRelativeUrl('', window.location.origin)
    navigate(relativeUri, { replace: true })
  }

  // every time the AuthProvider re-loads the AuthProviderWrapper receives
  // a new instance of oktaAuthConfig which provokes that almost the whole app
  // un-mounts and re-mounts again. Fixing it (by caching it in useMemo) would imply
  // re-thinking how we sort the ag grids, since setCollectionId (dispatched when we filter by collection)
  // would only provoke that we add or delete rows from the grid, instead of destroying and creating the grid

  const oktaAuthConfig = new OktaAuth({
    issuer: authServer,
    clientId: applicationId,
    redirectUri: redirectUri,
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    tokenManager: {
      storage: 'sessionStorage'
    }
  })

  return (
    <AuthProviderWrapper oktaAuth={oktaAuthConfig} onAuthRequired={customAuthHandler} restoreOriginalUri={restoreOriginalUri}>
      {props.children}
    </AuthProviderWrapper>
  )
}
